import { isEmpty } from '@ember/utils';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { replacePronouns } from '@linc-technologies/linced-common/utils/pronouns';
import moment from 'moment';
import { SuspensionReason } from '@linc-technologies/linced-common/enums/suspensions';
import { convertParagraphsToListItems } from '@linc-technologies/linced-common/utils/strings';
import ResponseActivityGroup from '@linc-technologies/linced-common/enums/response-activity-group';

// GetRecord is a record the templates read through get(), as they use nested
// paths (e.g. 'physicalAddress.street', 'fields.core:emailAddress.valueString').
interface GetRecord {
  get(key: string): unknown;
}

interface BehaviourDetails {
  delName: string;
  principal: { name: string; signature: string };
  nominatedSchoolPerson: GetRecord;
  doePhoneNumber: string;
}

interface ResponseActivity {
  responseActivity: number;
  completedDate: string;
  detail?: string;
}

interface AttendanceDetails {
  author: GetRecord;
  nominatedSchoolPerson?: GetRecord | null;
  absences: number | string;
  responseActivities?: ResponseActivity[] | null;
  learnerFirstName: string;
  followUpDate?: string | null;
  previousMeetingDate?: string | null;
  futureMeetingDate?: string | null;
}

interface ExtensionDetails {
  dayCount: number;
  newEndDate: string;
  nextSchoolDay: string;
}

// TemplateOptions holds what a template's placeholders are filled from. Each
// is optional; a placeholder with no value is left as it is.
export interface TemplateOptions {
  person?: GetRecord | null;
  caregiver?: GetRecord | null;
  school?: GetRecord | null;
  behaviour?: BehaviourDetails | null;
  suspension?: GetRecord | null;
  expulsion?: GetRecord | null;
  extension?: ExtensionDetails | null;
  attendance?: AttendanceDetails | null;
  preview?: boolean;
}

// A placeholder group's values, keyed by placeholder key.
type ReplacementGroup = Record<string, unknown>;

// The placeholder groups a template's placeholders read, and the records its
// field placeholders read.
interface Replacements {
  basic: ReplacementGroup;
  behaviour: ReplacementGroup | null;
  suspension: ReplacementGroup | null;
  expulsion: ReplacementGroup | null;
  extension: ReplacementGroup | null;
  attendance: ReplacementGroup | null;
  person?: GetRecord | null;
  caregiver?: GetRecord | null;
}

export function processTemplate(
  template: string,
  options: TemplateOptions,
): string {
  let processed = template.replaceAll(
    /(<[^>]+) data-contenteditable=/g,
    '$1 contenteditable=',
  );
  const defaults = {
    person: null,
    caregiver: null,
    school: null,
    behaviour: null,
    suspension: null,
    expulsion: null,
    extension: null,
    preview: false,
  };
  const opts: TemplateOptions = Object.assign(defaults, options);

  const now = moment();
  const replacements: Replacements = {
    basic: {
      date_short: now.format('DD/MM/YYYY'),
      date_long: now.format('dddd Do MMMM, YYYY'),
      time_12hr: now.format('hh:mm a'),
      time_24hr: now.format('HH:mm'),
    },
    person: opts.person,
    caregiver: opts.caregiver,
    behaviour: generateBehaviourReplacements(opts.behaviour),
    suspension: generateSuspensionReplacements(opts.suspension),
    expulsion: generateExpulsionReplacements(opts.expulsion),
    extension: generateExtensionReplacements(opts.extension),
    attendance: generateAttendanceReplacements(opts.attendance),
  };

  if (opts.school) {
    replacements.basic = Object.assign(
      replacements.basic,
      generateSchoolReplacements(opts.school),
    );
  }
  processed = processPlaceholders(processed, replacements);

  let gender = 'N';
  if (opts.person && !isEmpty(opts.person.get('preferredGender'))) {
    gender = opts.person.get('preferredGender') as string;
  }
  processed = replacePronouns(
    processed,
    gender,
    false,
    isEmpty(opts.person),
    'hero-template-placeholder',
  );

  if (opts.person && gender === 'N') {
    // We have a person, who may need "FIRSTNAME" replaced with their name after
    // the pronoun processing above.
    processed = processed.replaceAll(
      /\bFIRSTNAME\b/g,
      opts.person.get('firstName') as string,
    );
  }

  return processed;
}

function generateSchoolReplacements(school: GetRecord): ReplacementGroup {
  return {
    school_name: school.get('name'),
    school_phone: school.get('phone'),
    school_email: school.get('emailAddress'),

    physical_number: school.get('physicalAddress.number'),
    physical_street: school.get('physicalAddress.street'),
    physical_locality: school.get('physicalAddress.locality'),
    physical_district: school.get('physicalAddress.district'),
    physical_postcode: school.get('physicalAddress.postcode'),

    postal_number: school.get('postalAddress.number'),
    postal_street: school.get('postalAddress.street'),
    postal_locality: school.get('postalAddress.locality'),
    postal_district: school.get('postalAddress.district'),
    postal_postcode: school.get('postalAddress.postcode'),
  };
}

function generateBehaviourReplacements(
  behaviour: BehaviourDetails | null | undefined,
): ReplacementGroup | null {
  if (!behaviour) {
    return null;
  }
  return {
    del_name: behaviour.delName,
    principal_name: behaviour.principal.name,
    principal_sig: behaviour.principal.signature,
    nominated_school_person_name:
      behaviour.nominatedSchoolPerson.get('fullName'),
    nominated_school_person_email: behaviour.nominatedSchoolPerson.get(
      'fields.core:emailAddress.valueString',
    ),
    doe_phone_number: behaviour.doePhoneNumber,
  };
}

function generateAttendanceReplacements(
  attendance: AttendanceDetails | null | undefined,
): ReplacementGroup | null {
  if (!attendance) {
    return null;
  }

  let nominatedPersonName: unknown = null;
  let nominatedPersonEmail: unknown = null;

  const authorName = attendance.author.get('fullName');
  const authorEmail = attendance.author.get(
    'fields.core:emailAddress.valueString',
  );
  if (attendance.nominatedSchoolPerson) {
    nominatedPersonName = attendance.nominatedSchoolPerson.get('fullName');
    nominatedPersonEmail = attendance.nominatedSchoolPerson.get(
      'fields.core:emailAddress.valueString',
    );
  }

  return {
    author_name: authorName,
    author_email: authorEmail,
    nominated_school_person_name: nominatedPersonName,
    nominated_school_person_email: nominatedPersonEmail,
    attendance_days_absent: attendance.absences,
    response_activities: formatResponseActivities(
      attendance.responseActivities,
      attendance.learnerFirstName,
    ),
    follow_up_date: toDisplayDate(attendance.followUpDate),
    previous_meeting_date: toDisplayDate(attendance.previousMeetingDate),
    future_meeting_date: toDisplayDate(attendance.futureMeetingDate),
  };
}

function formatResponseActivities(
  responseActivities: ResponseActivity[] | null | undefined,
  learnerFirstName: string,
): string {
  if (!responseActivities?.length) {
    return `<span></span>`;
  }

  const items = responseActivities.map((activity) =>
    createResponseActivityListItem(activity),
  );

  return `<p>School staff have engaged with you on the occasions listed below to discuss these ongoing absences and attempt to get ${learnerFirstName}'s attendance back on track:</p>
        <ul>${items.join('')}</ul>`;
}

function createResponseActivityListItem(activity: ResponseActivity): string {
  const label = ResponseActivityGroup.getLabel(activity.responseActivity);

  const formattedDate = toDisplayDate(activity.completedDate);

  if (activity.responseActivity === ResponseActivityGroup.OTHER) {
    return `<li>${formattedDate}: ${activity.detail}</li>`;
  }

  let description = `${formattedDate}: ${label}`;
  if (!isEmpty(activity.detail)) {
    description += ` (${activity.detail})`;
  }
  return `<li>${description}</li>`;
}

function generateSuspensionReplacements(
  suspension: GetRecord | null | undefined,
): ReplacementGroup | null {
  if (!suspension) {
    return null;
  }
  const days = suspension.get('duration') as number;
  let duration = days + ' day';
  if (days > 1) {
    duration = duration + 's';
  }
  return {
    suspension_duration: duration,
    suspension_start_date: toDisplayDate(suspension.get('startDate') as string),
    suspension_end_date: toDisplayDate(suspension.get('endDate') as string),
    suspension_return_date: toDisplayDate(
      suspension.get('nextSchoolDay') as string,
    ),
    suspension_ground:
      suspension.get('writtenReason') ||
      SuspensionReason.getLabel(suspension.get('reason')),
    behavioural_expectations: suspension.get('behaviourExpectations'),
    suspension_factual_details_of_concern: convertParagraphsToListItems(
      suspension.get('behaviourDetails') as string,
    ),
  };
}

function generateExtensionReplacements(
  extension: ExtensionDetails | null | undefined,
): ReplacementGroup | null {
  if (!extension) {
    return null;
  }
  return {
    extension_duration: extension.dayCount,
    extension_end_date: toDisplayDate(extension.newEndDate),
    extension_return_date: toDisplayDate(extension.nextSchoolDay),
  };
}

function generateExpulsionReplacements(
  expulsion: GetRecord | null | undefined,
): ReplacementGroup | null {
  if (!expulsion) {
    return null;
  }
  return {
    specific_details_of_documents: expulsion.get('specificDetailsOfDocuments'),
    reasons_for_decision: expulsion.get('reasonForDecision'),
    expulsion_factual_details_of_concern: expulsion.get(
      'factualDetailsOfBehavioursOfConcern',
    ),
    expulsion_suspension_start_date: toDisplayDate(
      expulsion.get('suspensionStartDate') as string,
    ),
    expulsion_suspension_end_date: toDisplayDate(
      expulsion.get('suspensionEndDate') as string,
    ),
    expulsion_suspension_duration: expulsion.get('suspensionDuration'),
    notice_date: toDisplayDate(expulsion.get('noticeDate') as string),
    caregiver_meeting_date: toDisplayDate(
      expulsion.get('caregiverMeetingDate') as string,
    ),
  };
}

function processPlaceholders(
  content: string,
  replacements: Replacements,
): string {
  const placeholderRegex =
    /<span[^>]+?(data-placeholder-[^>]+)>(<span class="material-icons">.+?<\/span>)?([^<]+?)<\/span>/g;
  const typeRegex = /\bdata-placeholder-type="([^"]+?)"/;
  const personTypeRegex = /\bdata-placeholder-person-type="([^"]+?)"/;
  const keyRegex = /\bdata-placeholder-for="([^"]+?)"/;
  return content.replaceAll(placeholderRegex, (placeholder, attrs: string) => {
    const type = attrs.match(typeRegex)?.[1];
    const personType = attrs.match(personTypeRegex)?.[1];
    const key = attrs.match(keyRegex)?.[1];

    if (!type || !key) {
      // Couldn't determine the placeholder type or key.  Don't replace it.
      return placeholder;
    }
    switch (type) {
      case 'basic':
      case 'behaviour':
      case 'attendance':
      case 'expulsion':
      case 'suspension':
      case 'extension': {
        const value = replacements[type]?.[key];
        if (!isEmpty(value)) {
          // We have a matching replacement.  Use it.
          return String(value);
        }
        break;
      }

      case 'field':
        if (!personType) {
          // We can't tell which person type this is for.  Don't replace.
          return placeholder;
        }
        switch (parseInt(personType, 10)) {
          case PersonType.LEARNER:
          case PersonType.STAFF:
            if (replacements.person) {
              return (
                (replacements.person.get(`fields.${key}.value`) as
                  string | undefined) || ''
              );
            }
            break;

          case PersonType.CAREGIVER:
            if (replacements.caregiver) {
              return (
                (replacements.caregiver.get(`fields.${key}.value`) as
                  string | undefined) || ''
              );
            }
            break;
        }
    }

    // No matching replacement.  Don't replace.
    return placeholder;
  });
}

function toDisplayDate(date: string | null | undefined): string | null {
  if (isEmpty(date)) {
    return null;
  }
  return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
}
